/**
 * Document Overlay Module for AppBouncer SDK
 * Draws simplified document capture guidelines on the canvas
 *
 * Design: Same for all document types
 * - Dashed rounded border with accent blue color
 * - Semi-transparent overlay to highlight capture area
 */

// ============================================================
// Types
// ============================================================

export type DocumentType =
  | 'id_card'
  | 'passport'
  | 'drivers_license'
  | 'government_id'
  | 'national_id'
  | 'residence_permit';

export interface DocumentOverlayConfig {
  /** Background dimming color */
  overlayColor: string;
  /** Border color (dashed line) */
  borderColor: string;
  /** Highlight/active border color */
  highlightColor: string;
  /** Border width in pixels */
  borderWidth: number;
  /** Border corner radius in pixels */
  borderRadius: number;
  /** Dash pattern [dash length, gap length] */
  dashPattern: [number, number];
  /** Size as percentage of canvas (0.0-1.0) */
  sizeRatio: number;
  /** Aspect ratio (width / height) */
  aspectRatio: number;
}

export interface DocumentDimensions {
  /** Aspect ratio (width / height) */
  aspectRatio: number;
  /** Size as percentage of canvas (0.0-1.0) */
  sizeRatio: number;
}

// ============================================================
// Constants
// ============================================================

export const DEFAULT_OVERLAY_CONFIG: DocumentOverlayConfig = {
  overlayColor: 'rgba(0, 0, 0, 0.25)', // Very light - camera clearly visible
  borderColor: '#0077D8', // Accent blue dashed border
  highlightColor: '#00D4FF', // Capture flash color
  borderWidth: 6, // 6px dashed border
  borderRadius: 24, // More rounded corners
  dashPattern: [16, 12], // Dash length, gap length
  sizeRatio: 0.92, // Large frame (92% of canvas)
  aspectRatio: 1.586, // Standard ID card / driver's license ratio
};

// Legacy exports for backwards compatibility
export const DOCUMENT_DIMENSIONS: Record<DocumentType, DocumentDimensions> = {
  id_card: { aspectRatio: 1.586, sizeRatio: 0.88 },
  drivers_license: { aspectRatio: 1.586, sizeRatio: 0.88 },
  government_id: { aspectRatio: 1.586, sizeRatio: 0.88 },
  national_id: { aspectRatio: 1.586, sizeRatio: 0.88 },
  residence_permit: { aspectRatio: 1.586, sizeRatio: 0.88 },
  passport: { aspectRatio: 1.586, sizeRatio: 0.88 }, // Now same as others
};

export const PASSPORT_ZONES = {}; // Deprecated - no longer used
export const ID_CARD_ZONES = {}; // Deprecated - no longer used

// ============================================================
// Drawing Functions
// ============================================================

/**
 * Calculate the document rectangle position and size within the canvas
 * Universal for all document types
 */
export function calculateDocumentRect(
  canvasWidth: number,
  canvasHeight: number,
  _documentType?: DocumentType // Ignored - same for all types
): { x: number; y: number; width: number; height: number } {
  const aspectRatio = DEFAULT_OVERLAY_CONFIG.aspectRatio;
  const sizeRatio = DEFAULT_OVERLAY_CONFIG.sizeRatio;

  // Calculate the maximum document size that fits in the canvas
  const maxWidth = canvasWidth * sizeRatio;
  const maxHeight = canvasHeight * sizeRatio;

  let docWidth: number;
  let docHeight: number;

  // Fit document within bounds while maintaining aspect ratio
  if (maxWidth / aspectRatio <= maxHeight) {
    docWidth = maxWidth;
    docHeight = maxWidth / aspectRatio;
  } else {
    docHeight = maxHeight;
    docWidth = maxHeight * aspectRatio;
  }

  // Center the document
  const x = (canvasWidth - docWidth) / 2;
  const y = (canvasHeight - docHeight) / 2;

  return { x, y, width: docWidth, height: docHeight };
}

/**
 * Draw simplified document capture overlay
 * Same design for all document types:
 * - Dashed rounded border with accent blue color
 */
export function drawDocumentOverlay(
  ctx: CanvasRenderingContext2D,
  documentType: DocumentType,
  options: {
    highlight?: boolean;
    label?: string;
    config?: Partial<DocumentOverlayConfig>;
  } = {}
): void {
  const cfg = { ...DEFAULT_OVERLAY_CONFIG, ...options.config };
  const { highlight = false } = options;

  const w = ctx.canvas.width;
  const h = ctx.canvas.height;

  // Clear canvas
  ctx.clearRect(0, 0, w, h);

  // Calculate document rectangle (universal for all types)
  const docRect = calculateDocumentRect(w, h);

  // Draw semi-transparent overlay over entire canvas
  ctx.fillStyle = cfg.overlayColor;
  ctx.fillRect(0, 0, w, h);

  // Clear the document area (create transparent window with rounded corners)
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(docRect.x, docRect.y, docRect.width, docRect.height, cfg.borderRadius);
  ctx.clip();
  ctx.clearRect(docRect.x, docRect.y, docRect.width, docRect.height);
  ctx.restore();

  // Draw dashed border around the document frame
  const borderColor = highlight ? cfg.highlightColor : cfg.borderColor;
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = cfg.borderWidth;
  ctx.setLineDash(cfg.dashPattern);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  ctx.beginPath();
  ctx.roundRect(docRect.x, docRect.y, docRect.width, docRect.height, cfg.borderRadius);
  ctx.stroke();

  ctx.setLineDash([]); // Reset dash pattern
}

/**
 * Clear the document overlay canvas
 */
export function clearDocumentOverlay(ctx: CanvasRenderingContext2D): void {
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
}

/**
 * Get the recommended camera container aspect ratio for a document type
 * This can be used to adjust the camera preview container
 */
export function getRecommendedContainerAspect(_documentType?: DocumentType): number {
  // Return a reasonable aspect for camera container (4:3)
  return 4 / 3;
}
